/**
 * User settings runtime utilities and constants.
 *
 * This module contains runtime code for user preferences extracted from cardUtils.ts.
 * Settings-related constants don't belong with card utilities.
 * For type definitions, see types/card.ts.
 */
import type { Currency, PurchaseCurrency, Theme, UserSettings, ViewMode } from '@/types/card';

// ============================================================================
// User Settings Defaults
// ============================================================================

export const DEFAULT_USER_SETTINGS: UserSettings = {
  currency: 'USD',
  defaultView: 'grid',
  theme: 'system',
  compactTable: false,
  defaultCondition: 'NM',
  defaultLanguage: 'en',
};

// ============================================================================
// Currency Labels
// ============================================================================

export const CURRENCY_LABELS: Record<Currency, string> = {
  USD: 'US Dollar ($)',
  EUR: 'Euro (€)',
};

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  USD: '$',
  EUR: '€',
};

// ============================================================================
// Purchase Currency Labels & Symbols (user-entered purchase prices)
// ============================================================================

export const PURCHASE_CURRENCY_SYMBOLS: Record<PurchaseCurrency, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'C$',
  AUD: 'A$',
  JPY: '¥',
};

export const PURCHASE_CURRENCY_OPTIONS: { value: PurchaseCurrency; label: string }[] = [
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'GBP', label: 'GBP' },
  { value: 'CAD', label: 'CAD' },
  { value: 'AUD', label: 'AUD' },
  { value: 'JPY', label: 'JPY' },
];

// ============================================================================
// View Mode Labels
// ============================================================================

export const VIEW_MODE_LABELS: Record<ViewMode, string> = {
  grid: 'Grid',
  table: 'Table',
};

// ============================================================================
// Theme Labels
// ============================================================================

export const THEME_LABELS: Record<Theme, string> = {
  system: 'System',
  light: 'Light',
  dark: 'Dark',
};
